import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, query, where, orderBy, getDocs, doc, updateDoc, 
  addDoc, serverTimestamp, getDoc, limit, startAfter, deleteDoc
} from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { sendGameTestingReminders } from '../../emailjs/emailService';

// Collection references
const testingReportsRef = collection(db, 'gameTesting/testingReports/testingReports');
const testingStatusRef = collection(db, 'gameTesting/testingStatus/testingStatus');
const bugReportsRef = collection(db, 'gameTesting/bugReports/bugReports');

// Initial state
const initialState = {
  // User reports
  reports: [],
  loading: false,
  error: null,
  uniqueWeeks: [],
  selectedReport: null,
  bugReports: [],
  bugReportsLoading: false,
  bugReportsError: null,
  
  // Admin-only state
  adminReports: [],
  adminReportsLoading: false,
  adminReportsError: null,
  adminTotalReports: 0,
  adminLastVisible: null,
  adminHasMore: true,
  
  // Employees and reminders state
  employees: [],
  employeesLoading: false,
  employeesWithSubmissions: {},
  submissionsLoading: false,
  submissionsError: null,
  reminderSending: false,
  reminderError: null
};

// Async thunk to fetch game testing reports for current user
export const fetchReports = createAsyncThunk(
  'gameTesting/fetchReports',
  async ({ itemsPerPage = 10, lastVisible = null, filters = {}, userOnly = false }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      let q;
      
      // Base query with sorting and pagination
      q = query(
        testingReportsRef, 
        orderBy('createdAt', 'desc'),
        limit(itemsPerPage)
      );
      
      // If userOnly flag is true, only fetch reports for the current user
      if (userOnly && auth.user?.email) {
        q = query(
          q, 
          where('assignedTo.email', '==', auth.user.email)
        );
      }
      
      // Apply filters if provided
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }
      
      if (filters.weekRange) {
        q = query(q, where('weekRange', '==', filters.weekRange));
      }
      
      // Apply pagination
      if (lastVisible) {
        q = query(q, startAfter(lastVisible));
      }
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return { 
          reports: [],
          lastVisible: null,
          hasMore: false
        };
      }
      
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt) || new Date(),
        lastUpdated: doc.data().lastUpdated?.toDate?.() || new Date(doc.data().lastUpdated) || new Date()
      }));
      
      // Extract unique week ranges
      const uniqueWeeks = [...new Set(reportsData.map(report => report.weekRange))];
      
      // Check if there are more documents
      const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
      
      let hasMore = false;
      if (newLastVisible) {
        const nextQuery = query(
          q,
          startAfter(newLastVisible),
          limit(1)
        );
        
        const nextSnapshot = await getDocs(nextQuery);
        hasMore = !nextSnapshot.empty;
      }
      
      return {
        reports: reportsData,
        uniqueWeeks,
        lastVisible: newLastVisible,
        hasMore
      };
    } catch (error) {
      console.error("Error fetching reports:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Admin: Fetch all testing reports (simplified)
export const fetchAllReports = createAsyncThunk(
  'gameTesting/fetchAllReports',
  async ({ itemsPerPage = 10, lastVisible = null, filters = {} }, { rejectWithValue }) => {
    try {
      // Simple query - no complex filtering to avoid duplicates
      let q = query(
        testingReportsRef, 
        orderBy('createdAt', 'desc'),
        limit(itemsPerPage)
      );
      
      // Apply pagination only if provided
      if (lastVisible) {
        q = query(q, startAfter(lastVisible));
      }
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return { 
          reports: [],
          lastVisible: null,
          hasMore: false,
          isFirstPage: !lastVisible
        };
      }
      
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt) || new Date(),
        lastUpdated: doc.data().lastUpdated?.toDate?.() || new Date(doc.data().lastUpdated) || new Date()
      }));
      
      // Check if there are more documents
      const nextQuery = query(
        testingReportsRef,
        orderBy('createdAt', 'desc'),
        startAfter(snapshot.docs[snapshot.docs.length - 1]),
        limit(1)
      );
      
      const nextSnapshot = await getDocs(nextQuery);
      const hasMore = !nextSnapshot.empty;
      
      return {
        reports: reportsData,
        lastVisible: snapshot.docs[snapshot.docs.length - 1],
        hasMore,
        isFirstPage: !lastVisible
      };
    } catch (error) {
      console.error("Error fetching all reports:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Admin: Fetch employees with their weekly submissions
export const fetchEmployeesWithSubmissions = createAsyncThunk(
  'gameTesting/fetchEmployeesWithSubmissions',
  async (_, { rejectWithValue }) => {
    try {
      // 1. Fetch all employees
      const employeesRef = collection(db, 'myeliners');
      const employeesSnapshot = await getDocs(employeesRef);
      
      if (employeesSnapshot.empty) {
        return { employees: [], submissionsByUser: {} };
      }
      
      const employees = employeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // 2. Get current week range
      const currentDate = new Date();
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      const currentWeekRange = `${format(weekStart, 'MMM d, yyyy')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      
      // 3. Fetch submissions for the current week
      const q = query(
        testingReportsRef,
        where('weekRange', '==', currentWeekRange)
      );
      
      const submissionsSnapshot = await getDocs(q);
      
      // Create a map of submissions by user
      const submissionsByUser = {};
      
      if (!submissionsSnapshot.empty) {
        submissionsSnapshot.forEach(doc => {
          const data = doc.data();
          const userEmail = data.assignedTo?.email;
          
          if (userEmail) {
            if (!submissionsByUser[userEmail]) {
              submissionsByUser[userEmail] = {
                submissions: 0,
                pendingReports: 0,
                reportIds: []
              };
            }
            
            submissionsByUser[userEmail].submissions++;
            submissionsByUser[userEmail].reportIds.push(doc.id);
            
            if (data.status === 'pending_testing') {
              submissionsByUser[userEmail].pendingReports++;
            }
          }
        });
      }
      
      return { employees, submissionsByUser };
    } catch (error) {
      console.error("Error fetching employees with submissions:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Admin: Send reminders to selected users
export const sendReminders = createAsyncThunk(
  'gameTesting/sendReminders',
  async ({ selectedUsers, adminUser }, { rejectWithValue }) => {
    try {
      const sentReminders = [];
      const currentDate = new Date();
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      const currentWeekRange = `${format(weekStart, 'MMM d, yyyy')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      
      // Fetch user details for selected emails to have proper employee objects
      const usersRef = collection(db, 'users');
      const employeeData = [];
      
      for (const userEmail of selectedUsers) {
        // Get user data from Firestore
        const q = query(usersRef, where('email', '==', userEmail));
        const userSnapshot = await getDocs(q);
        
        let userData = {
          email: userEmail,
          firstName: userEmail.split('@')[0] // Fallback if no user document found
        };
        
        if (!userSnapshot.empty) {
          const user = userSnapshot.docs[0].data();
          userData = {
            ...userData,
            ...user,
            firstName: user.name ? user.name.split(' ')[0] : userData.firstName
          };
        }
        
        employeeData.push(userData);
        
        // Create empty testing report
        const reportData = {
          assignedTo: {
            email: userEmail,
            name: userData.name || userData.firstName
          },
          status: 'pending_testing',
          createdAt: serverTimestamp(),
          weekRange: currentWeekRange,
          selectedGames: [],
          tutorialFeedback: '',
          levelsFeedback: '',
          positiveAspects: '',
          improvementSuggestions: '',
          overallExperience: '',
          bugReports: [],
          lastUpdated: serverTimestamp(),
          submittedBy: {
            email: adminUser.email,
            name: adminUser.displayName || adminUser.email
          }
        };
        
        // Add the report
        const reportDocRef = await addDoc(testingReportsRef, reportData);
        
        // Create status tracking document with client-side timestamp for history
        // to avoid "serverTimestamp() is not currently supported inside arrays" error
        const currentClientDate = new Date();
        
        const statusData = {
          reportId: reportDocRef.id,
          status: 'pending_testing',
          updatedAt: serverTimestamp(),
          updatedBy: {
            email: adminUser.email,
            name: adminUser.displayName || adminUser.email
          },
          weekRange: currentWeekRange,
          history: [{
            status: 'pending_testing',
            timestamp: currentClientDate, // Use client-side timestamp instead of serverTimestamp()
            updatedBy: {
              email: adminUser.email,
              name: adminUser.displayName || adminUser.email
            }
          }]
        };
        
        await addDoc(testingStatusRef, statusData);
        
        sentReminders.push({
          email: userEmail,
          reportId: reportDocRef.id,
          weekRange: currentWeekRange
        });
      }
      
      // Send email notifications using the emailService
      try {
        await sendGameTestingReminders(employeeData);
        console.log('Game testing reminder emails sent successfully');
      } catch (emailError) {
        console.error('Error sending game testing reminder emails:', emailError);
        // Continue with the function even if emails fail - we don't want to revert the created reports
      }
      
      return sentReminders;
    } catch (error) {
      console.error("Error sending reminders:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Admin: Update report status
export const updateReportStatus = createAsyncThunk(
  'gameTesting/updateReportStatus',
  async ({ reportId, newStatus, adminUser }, { rejectWithValue }) => {
    try {
      // Get the report
      const reportDocRef = doc(testingReportsRef, reportId);
      const reportSnapshot = await getDoc(reportDocRef);
      
      if (!reportSnapshot.exists()) {
        return rejectWithValue('Report not found');
      }
      
      const reportData = reportSnapshot.data();
      
      // Update the report status
      await updateDoc(reportDocRef, {
        status: newStatus,
        lastUpdated: serverTimestamp()
      });
      
      // Get the status document
      const statusQuery = query(
        testingStatusRef,
        where('reportId', '==', reportId)
      );
      
      const statusSnapshot = await getDocs(statusQuery);
      
      // Use client-side timestamp for history entries to avoid the Firestore error
      const currentClientDate = new Date();
      
      if (statusSnapshot.empty) {
        // Create a new status document if none exists
        const newStatusData = {
          reportId,
          status: newStatus,
          updatedAt: serverTimestamp(),
          updatedBy: {
            email: adminUser.email,
            name: adminUser.displayName || adminUser.email
          },
          weekRange: reportData.weekRange,
          history: [{
            status: newStatus,
            timestamp: currentClientDate, // Use client-side timestamp instead of serverTimestamp()
            updatedBy: {
              email: adminUser.email,
              name: adminUser.displayName || adminUser.email
            }
          }]
        };
        
        await addDoc(testingStatusRef, newStatusData);
      } else {
        // Update existing status document
        const statusDocRef = statusSnapshot.docs[0].ref;
        const currentStatusData = statusSnapshot.docs[0].data();
        
        await updateDoc(statusDocRef, {
          status: newStatus,
          updatedAt: serverTimestamp(),
          updatedBy: {
            email: adminUser.email,
            name: adminUser.displayName || adminUser.email
          },
          history: [
            ...currentStatusData.history,
            {
              status: newStatus,
              timestamp: currentClientDate, // Use client-side timestamp instead of serverTimestamp()
              updatedBy: {
                email: adminUser.email,
                name: adminUser.displayName || adminUser.email
              }
            }
          ]
        });
      }
      
      // Return updated report data
      return {
        id: reportId,
        ...reportData,
        status: newStatus,
        lastUpdated: new Date() // Keep as Date for immediate UI update
      };
    } catch (error) {
      console.error("Error updating report status:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to get specific report
export const getReport = createAsyncThunk(
  'gameTesting/getReport',
  async (reportId, { rejectWithValue }) => {
    try {
      const reportRef = doc(testingReportsRef, reportId);
      const reportSnap = await getDoc(reportRef);
      
      if (!reportSnap.exists()) {
        return rejectWithValue('Report not found');
      }
      
      const reportData = reportSnap.data();
      
      return {
        id: reportSnap.id,
        ...reportData,
        createdAt: reportData.createdAt?.toDate?.() || new Date(reportData.createdAt) || new Date(),
        lastUpdated: reportData.lastUpdated?.toDate?.() || new Date(reportData.lastUpdated) || new Date()
      };
    } catch (error) {
      console.error("Error getting report:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update report
export const updateReport = createAsyncThunk(
  'gameTesting/updateReport',
  async ({ reportId, reportData, files = [], directImages = null }, { rejectWithValue }) => {
    try {
      // Upload files if any
      const uploadedFiles = [];
      
      for (const file of files) {
        const fileExtension = file.name.split('.').pop();
        const fileName = `game-testing/${reportId}/${uuidv4()}.${fileExtension}`;
        const storageRef = ref(storage, fileName);
        
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        
        uploadedFiles.push({
          url: downloadURL,
          fileName,
          originalName: file.name,
          type: file.type
        });
      }
      
      // Get existing report to preserve any fields we're not updating
      const reportRef = doc(testingReportsRef, reportId);
      const existingReport = await getDoc(reportRef);
      
      if (!existingReport.exists()) {
        return rejectWithValue('Report not found');
      }
      
      const existingData = existingReport.data();
      
      // Use directImages if provided, otherwise combine existing and new images/files
      const finalImages = directImages !== null 
        ? directImages 
        : [
            ...(existingData.images || []),
            ...uploadedFiles
          ];
      
      // Update report
      await updateDoc(reportRef, {
        ...reportData,
        images: finalImages,
        lastUpdated: serverTimestamp(),
        status: reportData.status || 'test_report_available'
      });
      
      // Return updated report - keep the Firebase timestamp as is
      return {
        id: reportId,
        ...existingData,
        ...reportData,
        images: finalImages,
        // Don't override lastUpdated with client Date, let Firebase handle it
      };
    } catch (error) {
      console.error("Error updating report:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to add bug report
export const addBugReport = createAsyncThunk(
  'gameTesting/addBugReport',
  async ({ reportId, bugData, files = [] }, { rejectWithValue }) => {
    try {
      // Upload files if any
      const uploadedFiles = [];
      
      for (const file of files) {
        const fileExtension = file.name.split('.').pop();
        const fileName = `bug-reports/${reportId}/${uuidv4()}.${fileExtension}`;
        const storageRef = ref(storage, fileName);
        
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        
        uploadedFiles.push({
          url: downloadURL,
          fileName,
          originalName: file.name,
          type: file.type
        });
      }
      
      // Add to bug reports collection
      const bugReportData = {
        ...bugData,
        images: uploadedFiles,
        reportId,
        submittedAt: serverTimestamp(),
        status: 'pending'
      };
      
      const bugDocRef = await addDoc(bugReportsRef, bugReportData);
      
      // Update the report document to include the bug report
      const reportRef = doc(testingReportsRef, reportId);
      const reportSnap = await getDoc(reportRef);
      
      if (!reportSnap.exists()) {
        return rejectWithValue('Report not found');
      }
      
      const reportData = reportSnap.data();
      const existingBugReports = reportData.bugReports || [];
      
      await updateDoc(reportRef, {
        bugReports: [
          ...existingBugReports,
          {
            ...bugReportData,
            id: bugDocRef.id,
            submittedAt: new Date(),
          }
        ],
        lastUpdated: serverTimestamp()
      });
      
      return {
        ...bugReportData,
        id: bugDocRef.id,
        submittedAt: new Date(), // Keep this as Date for immediate UI update
      };
    } catch (error) {
      console.error("Error adding bug report:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to delete report with all associated data
export const deleteReport = createAsyncThunk(
  'gameTesting/deleteReport',
  async (reportId, { rejectWithValue }) => {
    try {
      // Get the report to access file paths and bug reports
      const reportRef = doc(testingReportsRef, reportId);
      const reportSnapshot = await getDoc(reportRef);
      
      if (!reportSnapshot.exists()) {
        return rejectWithValue('Report not found');
      }
      
      const reportData = reportSnapshot.data();
      
      // Delete all associated images from storage
      if (reportData.images && reportData.images.length > 0) {
        for (const image of reportData.images) {
          try {
            // Extract file path from URL or use fileName if available
            const filePath = image.fileName || image.url.split('/o/')[1]?.split('?')[0];
            if (filePath) {
              const storageRef = ref(storage, decodeURIComponent(filePath));
              await deleteObject(storageRef);
            }
          } catch (storageError) {
            console.warn('Failed to delete image:', image.url, storageError);
            // Continue with deletion even if some files fail
          }
        }
      }
      
      // Delete all associated bug reports
      if (reportData.bugReports && reportData.bugReports.length > 0) {
        const bugReportQueries = query(
          bugReportsRef,
          where('reportId', '==', reportId)
        );
        
        const bugReportsSnapshot = await getDocs(bugReportQueries);
        const deleteBugPromises = bugReportsSnapshot.docs.map(bugDoc => 
          deleteDoc(doc(bugReportsRef, bugDoc.id))
        );
        
        await Promise.all(deleteBugPromises);
      }
      
      // Delete the main report document
      await deleteDoc(reportRef);
      
      return { reportId };
    } catch (error) {
      console.error("Error deleting report:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Add a proper async thunk version of resetAdminReports
export const resetAdminReportsThunk = createAsyncThunk(
  'gameTesting/resetAdminReportsThunk',
  async () => {
    // This is just a simple thunk that doesn't do any async work
    // but returns the expected data structure
    return { success: true };
  }
);

const gameTestingSlice = createSlice({
  name: 'gameTesting',
  initialState,
  reducers: {
    clearSelectedReport: (state) => {
      state.selectedReport = null;
    },
    setSelectedReport: (state, action) => {
      state.selectedReport = action.payload;
    },
    resetAdminReports: (state) => {
      state.adminReports = [];
      state.adminLastVisible = null;
      state.adminHasMore = true;
    },
    clearAdminErrors: (state) => {
      state.adminReportsError = null;
      state.submissionsError = null;
      state.reminderError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch reports
      .addCase(fetchReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.reports;
        state.uniqueWeeks = action.payload.uniqueWeeks;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Admin: Fetch all reports
      .addCase(fetchAllReports.pending, (state) => {
        state.adminReportsLoading = true;
        state.adminReportsError = null;
      })
      .addCase(fetchAllReports.fulfilled, (state, action) => {
        state.adminReportsLoading = false;
        
        // Simple logic: if it's the first page, replace; otherwise append
        if (action.payload.isFirstPage) {
          state.adminReports = action.payload.reports;
        } else {
          // Append and ensure no duplicates by filtering out existing IDs
          const existingIds = new Set(state.adminReports.map(r => r.id));
          const newReports = action.payload.reports.filter(r => !existingIds.has(r.id));
          state.adminReports = [...state.adminReports, ...newReports];
        }
        
        state.adminLastVisible = action.payload.lastVisible;
        state.adminHasMore = action.payload.hasMore;
      })
      .addCase(fetchAllReports.rejected, (state, action) => {
        state.adminReportsLoading = false;
        state.adminReportsError = action.payload;
      })
      
      // Admin: Fetch employees with submissions
      .addCase(fetchEmployeesWithSubmissions.pending, (state) => {
        state.employeesLoading = true;
        state.submissionsLoading = true;
        state.submissionsError = null;
      })
      .addCase(fetchEmployeesWithSubmissions.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.submissionsLoading = false;
        state.employees = action.payload.employees;
        state.employeesWithSubmissions = action.payload.submissionsByUser;
      })
      .addCase(fetchEmployeesWithSubmissions.rejected, (state, action) => {
        state.employeesLoading = false;
        state.submissionsLoading = false;
        state.submissionsError = action.payload;
      })
      
      // Admin: Send reminders
      .addCase(sendReminders.pending, (state) => {
        state.reminderSending = true;
        state.reminderError = null;
      })
      .addCase(sendReminders.fulfilled, (state) => {
        state.reminderSending = false;
      })
      .addCase(sendReminders.rejected, (state, action) => {
        state.reminderSending = false;
        state.reminderError = action.payload;
      })
      
      // Admin: Update report status
      .addCase(updateReportStatus.pending, (state) => {
        state.adminReportsLoading = true;
      })
      .addCase(updateReportStatus.fulfilled, (state, action) => {
        state.adminReportsLoading = false;
        
        // Update status in the reports list
        const reportIndex = state.adminReports.findIndex(report => report.id === action.payload.id);
        if (reportIndex !== -1) {
          state.adminReports[reportIndex] = {
            ...state.adminReports[reportIndex],
            ...action.payload
          };
        }
        
        // Also update the selected report if it matches
        if (state.selectedReport && state.selectedReport.id === action.payload.id) {
          state.selectedReport = {
            ...state.selectedReport,
            ...action.payload
          };
        }
      })
      .addCase(updateReportStatus.rejected, (state, action) => {
        state.adminReportsLoading = false;
        state.adminReportsError = action.payload;
      })
      
      // Get report
      .addCase(getReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReport.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedReport = action.payload;
      })
      .addCase(getReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update report
      .addCase(updateReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReport.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedReport = action.payload;
        
        // Update in the reports array too
        const index = state.reports.findIndex(report => report.id === action.payload.id);
        if (index !== -1) {
          state.reports[index] = action.payload;
        }
        
        // Also update in admin reports if present
        const adminIndex = state.adminReports.findIndex(report => report.id === action.payload.id);
        if (adminIndex !== -1) {
          state.adminReports[adminIndex] = action.payload;
        }
      })
      .addCase(updateReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add bug report
      .addCase(addBugReport.pending, (state) => {
        state.bugReportsLoading = true;
        state.bugReportsError = null;
      })
      .addCase(addBugReport.fulfilled, (state, action) => {
        state.bugReportsLoading = false;
        
        // Add to the bug reports array
        state.bugReports.push(action.payload);
        
        // Update the bug reports in the selected report
        if (state.selectedReport) {
          const bugReports = state.selectedReport.bugReports || [];
          state.selectedReport.bugReports = [...bugReports, action.payload];
        }
      })
      .addCase(addBugReport.rejected, (state, action) => {
        state.bugReportsLoading = false;
        state.bugReportsError = action.payload;
      })
      
      // Delete report
      .addCase(deleteReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReport.fulfilled, (state, action) => {
        state.loading = false;
        const reportId = action.payload.reportId;
        
        // Remove from reports array
        state.reports = state.reports.filter(report => report.id !== reportId);
        
        // Remove from admin reports array
        state.adminReports = state.adminReports.filter(report => report.id !== reportId);
        
        // Clear selected report if it was the deleted one
        if (state.selectedReport && state.selectedReport.id === reportId) {
          state.selectedReport = null;
        }
        
        // Remove bug reports for this report
        state.bugReports = state.bugReports.filter(bug => bug.reportId !== reportId);
      })
      .addCase(deleteReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Reset admin reports list - fix the reference to use the thunk
      .addCase(resetAdminReportsThunk.fulfilled, (state) => {
        state.reports = [];
        state.lastVisible = null;
        state.hasMore = false;
      });
  },
});

export const { clearSelectedReport, setSelectedReport, resetAdminReports, clearAdminErrors } = gameTestingSlice.actions;

export default gameTestingSlice.reducer; 